import {
  Box,
  Flex,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Stack,
  Text,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';
import { getPrettyTimeOfDay, prettyDate } from '@pelicargo/utils';
import { parseISO } from 'date-fns';
import { useMemo, useRef } from 'react';
import { FieldValues, useWatch } from 'react-hook-form';
import { HiCalendarDays } from 'react-icons/hi2';

import { SimpleField } from '../SimpleField';
import { Props, SimpleDatePicker } from './SimpleDatePicker';

export const SimpleDatePickerInput = <FormSchema extends FieldValues = FieldValues>({
  name,
  label,
  helper,
  variant,
  matchWidth = true,
  timeOfDay = false,
  timeOfDayName = undefined,
  iconFontSize = '24px',
  placeholder = 'Choose a date',
  fontSize = 'md',
  disablePastDates = false,
  ...rest
}: Props<FormSchema>) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const ref = useRef();
  useOutsideClick({ ref, handler: onClose });
  const formValue = useWatch({ name });
  const timeOfDayValue = useWatch({ name: timeOfDayName ?? '' });

  const handleOnChange = () => {
    if (!timeOfDay) onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onOpen();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const displayValue = useMemo(() => {
    if (!formValue) return placeholder;

    const formattedDate = prettyDate(parseISO(formValue), { timeZone: 'Etc/UTC' });

    return `${formattedDate}${timeOfDayValue ? `, ${getPrettyTimeOfDay(timeOfDayValue)}` : ''}`;
  }, [formValue, placeholder, timeOfDayValue]);

  return (
    <Box w="full" ref={ref}>
      <Popover isOpen={isOpen} onOpen={onOpen} matchWidth={matchWidth} closeOnBlur={false}>
        <PopoverTrigger>
          <SimpleField name={name} label={label} helper={helper} variant={variant} spacing={0} {...rest}>
            <Stack
              direction="row"
              alignItems="center"
              spacing="3"
              w="full"
              tabIndex={0}
              _focus={{
                outline: 'none',
                boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)',
                borderColor: 'secondary.500',
              }}
              onKeyDown={handleKeyDown}
            >
              <Icon as={HiCalendarDays} fontSize={iconFontSize} />
              <Text fontSize={fontSize} color={formValue ? 'text.default' : 'text.subtle'} w="full" noOfLines={1}>
                {displayValue}
              </Text>
            </Stack>
          </SimpleField>
        </PopoverTrigger>
        <Portal>
          <PopoverContent ref={ref} w="full">
            <Flex py="3" px="2" w="full" boxShadow="sm" flexDir="column">
              <SimpleDatePicker<FormSchema>
                name={name}
                timeOfDay={timeOfDay}
                timeOfDayName={timeOfDayName}
                onChange={handleOnChange}
                disablePastDates={disablePastDates}
              />
            </Flex>
          </PopoverContent>
        </Portal>
      </Popover>
    </Box>
  );
};
